import { createContext, useContext, type ReactNode } from 'react'

import { isShadowUser, toCustomerAreaRoute } from '@/utils/customer-shadow'

/**
 * Which customer the surrounding customer-area screens belong to.
 *
 * The advisor "shadow view" renders the same components the customer sees, so
 * every query, route and mutation underneath has to target that customer
 * rather than the signed-in advisor. Passing the id down as an optional prop
 * made forgetting it silent — a component without the prop simply read the
 * advisor's own data. Reading it from here instead means a screen cannot
 * accidentally opt out: outside the provider there is no shadow user, which
 * is exactly what the customer's own area needs.
 */
const ShadowUserContext = createContext<number | undefined>(undefined)

export function ShadowUserProvider({
  shadowUserId,
  children,
}: {
  shadowUserId: number | undefined
  children: ReactNode
}) {
  return (
    <ShadowUserContext.Provider
      value={isShadowUser(shadowUserId) ? shadowUserId : undefined}
    >
      {children}
    </ShadowUserContext.Provider>
  )
}

/** The shadowed customer, or `undefined` in the customer's own area. */
export function useShadowUserId(): number | undefined {
  return useContext(ShadowUserContext)
}

/** `true` while an advisor is looking at someone else's area. Read this where
 *  a surface must behave differently rather than merely fetch differently. */
export function useIsShadowView(): boolean {
  return useShadowUserId() !== undefined
}

/**
 * Rewrites a customer route so it stays inside the shadow view. The shadow
 * screen is looked up from the route itself, so a caller passes only where it
 * wants to go — see `CustomerLink`, which is what components should use.
 */
export function useCustomerHref(): (customerRoute: string) => string {
  const shadowUserId = useShadowUserId()
  return customerRoute => toCustomerAreaRoute(customerRoute, shadowUserId)
}
